import { useState, useCallback, useRef, useEffect } from 'react';
import { validateFile, formatFileSize } from '../utils/fileValidator';
import { API_CONFIG } from '../utils/apiConfig';
import {
  uploadFloorPlan,
  captureUploadArtifact,
  generateUploadCorrelationId,
} from '../utils/externalAiService';
import { getGuestSessionId } from '../utils/guestSession';
import { createCancellablePoller } from '../utils/statusPoller';
import { mapErrorToUI } from '../utils/errorMapper';
import {
  trackUploadStarted,
  trackUploadSuccess,
  trackUploadFailure,
  trackPollingStarted,
  trackPollingFinished,
  trackErrorEncountered,
  resetAnalytics,
} from '../utils/analytics';
import store from '../App/store';
import { addGuestLayout } from '../utils/guestLayoutStorage';
import { normalizeGalleryUrlForCurrentOrigin } from '../utils/galleryUrl';
import { useAnalytics } from '../utils/eventLogAnalytics';

/**
 * @param {Object} opts
 * @param {string} opts.source — analytics source label.
 * @param {string|null} opts.customerRef — Listing ID echoed from the iframe's
 *   `?ref=` query param (integration mode only). When present, it is forwarded
 *   to the upload service as `customer_ref` so the backend can auto-create
 *   the listing↔layout link, and attached to analytics events for rollups.
 *   Always optional — non-integration upload paths pass nothing and behavior
 *   is unchanged.
 */
export const useUploadFlow = ({ source, customerRef = null } = {}) => {
  const [state, setState] = useState('idle');
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [galleryUrl, setGalleryUrl] = useState(null);
  const [uniqueId, setUniqueId] = useState(null);
  const pollerRef = useRef(null);
  const uploadAbortControllerRef = useRef(null);
  // Dedicated controller for the capture-first request so it neither blocks nor
  // is blocked by a real upload; aborted on the same cancel/unmount lifecycle.
  const captureAbortControllerRef = useRef(null);
  const previewUrlRef = useRef(null);
  previewUrlRef.current = previewUrl;

  const { eventLog } = useAnalytics(customerRef || null);

  /**
   * Capture-first for client-validation rejects. Fire-and-forget: sends the raw
   * bytes to the backend capture endpoint so partner admins can later inspect
   * the original artifact on the failed row. Returns the correlation id (to be
   * stamped on the upload_failed event) or null when capture isn't attempted.
   *
   * Skips anything over the backend cap (multer would reject it). NEVER throws
   * and NEVER affects the validation message the uploader already saw.
   */
  const captureRejectedUpload = useCallback(
    (file, errorType, errorMessage) => {
      const eligible =
        errorType === 'INVALID_TYPE' || errorType === 'FILE_TOO_LARGE';
      if (!eligible || !file) return null;
      if (file.size > API_CONFIG.FILE_VALIDATION.CAPTURE_MAX_SIZE) return null;

      const correlationId = generateUploadCorrelationId();
      const controller = new AbortController();
      captureAbortControllerRef.current = controller;

      // Promise.resolve(...).catch swallows any rejection (capture failures stay
      // invisible to the user) while the request still fires non-blocking.
      Promise.resolve(
        captureUploadArtifact(file, {
          sessionId: correlationId,
          customerRef,
          errorType,
          errorMessage,
          signal: controller.signal,
        })
      ).catch(() => {});

      return correlationId;
    },
    [customerRef]
  );

  // `fileMeta` is forwarded so a polling-side failure can surface the file
  // identity (name + size) on the resulting `upload_failed` analytics event,
  // matching the metadata already logged on `floorplan_uploaded`.
  const startPolling = useCallback((jobIdToPoll, fileMeta = null, correlationId = null) => {
    trackPollingStarted(jobIdToPoll);

    const startTime = Date.now();
    const { promise, cancel } = createCancellablePoller(
      jobIdToPoll,
      (statusUpdate) => {
        setProcessingStatus(statusUpdate.status);
        setElapsedTime(statusUpdate.elapsedTime);
      }
    );

    pollerRef.current = { cancel };

    promise
      .then((result) => {
        const duration = Date.now() - startTime;
        trackPollingFinished({ jobId: jobIdToPoll, status: 'completed', duration });

        const normalizedGalleryUrl = normalizeGalleryUrlForCurrentOrigin(result?.galleryUrl ?? null);
        setState('done');
        setGalleryUrl(normalizedGalleryUrl);
        // Backend returns the SavedLayout uuid as `uniqueId` (see NewUpload.js,
        // UploadFloor.js). Previously read `_id`, which is absent from this
        // response shape, so `newId` was always null — breaking the guest-claim
        // write below. Fall back defensively through `uuid`/`_id` and finally
        // the gallery URL suffix (mirrors UploadFlowPanel's existing fallback).
        const newId =
          result?.data?.uniqueId ||
          result?.data?.uuid ||
          result?.data?._id ||
          (normalizedGalleryUrl && normalizedGalleryUrl.split('/').pop()) ||
          (result?.galleryUrl && result.galleryUrl.split('/').pop()) ||
          null;
        setUniqueId(newId);
        eventLog(window, 'floor_plan_event', 'gallery_ready', {
          uniqueId: newId,
          galleryUrl: normalizedGalleryUrl,
          thumbnailImage:
            result?.data?.furnishedImageId ||
            result?.data?.thumbnailFurnishedImage ||
            result?.data?.thumbnailImage ||
            result?.data?.image ||
            null,
          source,
        });
        // Guest-only: remember this uuid so the next successful auth can claim it.
        // Authenticated uploads carry Bearer and are already attached server-side.
        try {
          const hasToken = !!store.getState()?.roomNateSlice?.currentToken;
          if (!hasToken && newId) {
            addGuestLayout({
              uuid: newId,
              galleryUrl: normalizedGalleryUrl ?? undefined,
              source,
            });
          }
        } catch (err) {
          console.warn('[useUploadFlow] guest claim write failed (polling):', err);
        }
      })
      .catch((error) => {
        if (error?.isCancelled) {
          setState('idle');
          setSelectedFile(null);
          return;
        }

        const mappedError = mapErrorToUI(error);
        const duration = Date.now() - startTime;

        trackPollingFinished({ jobId: jobIdToPoll, status: 'failed', duration });

        if (mappedError?.shouldDisplay) {
          setUploadError(mappedError);
          setState('error');
          trackErrorEncountered({ ...mappedError, context: 'polling' });
          eventLog(window, 'floor_plan_event', 'upload_failed', {
            errorType: mappedError.errorType,
            errorMessage: mappedError.message,
            backendError: error.response?.data?.error,
            requestId: error.response?.data?.request_id,
            fileName: fileMeta?.name,
            fileSize: fileMeta?.size != null ? formatFileSize(fileMeta.size) : undefined,
            // Same correlation id the real upload sent as session_id, so the
            // server-captured artifact joins this polling-stage failure too.
            ...(correlationId ? { sessionId: correlationId } : {}),
          });
        } else {
          setState('idle');
        }
      });
  }, [source, eventLog]);

  const startUpload = useCallback(
    async (file) => {
      if (!file) return;

      setState('uploading');
      setUploadError(null);
      setUploadProgress(0);

      trackUploadStarted({ type: file.type, size: file.size });

      // Simple progress simulation until server response
      // Stretched to ~20 seconds to be more representative of actual processing time
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 2200);

      uploadAbortControllerRef.current = new AbortController();
      // One correlation id per attempt, sent as session_id on the real upload
      // so a server-stage failure's captured artifact joins via the same key.
      const correlationId = generateUploadCorrelationId();

      try {
        const response = await uploadFloorPlan(file, {
          customer_ref: customerRef,
          sessionId: correlationId,
          guestSessionId: getGuestSessionId(),
        });

        clearInterval(progressInterval);
        setUploadProgress(100);

        if (response?.jobId) {
          setJobId(response.jobId);
          trackUploadSuccess({ jobId: response.jobId });

          setState('processing');
          startPolling(response.jobId, { name: file?.name, size: file?.size }, correlationId);
          return;
        }

        if (response?.status === 'success' && response?.data) {
          trackUploadSuccess({ immediate: true });

          const normalizedGalleryUrl = normalizeGalleryUrlForCurrentOrigin(
            response.data?.galleryUrl ?? null
          );
          setState('done');
          setGalleryUrl(normalizedGalleryUrl);
          // See polling path above — response field is `uniqueId`, not `_id`.
          const newId =
            response.data?.uniqueId ||
            response.data?.uuid ||
            response.data?._id ||
            (normalizedGalleryUrl && normalizedGalleryUrl.split('/').pop()) ||
            (response.data?.galleryUrl && response.data.galleryUrl.split('/').pop()) ||
            null;
          setUniqueId(newId);
          eventLog(window, 'floor_plan_event', 'gallery_ready', {
            uniqueId: newId,
            galleryUrl: normalizedGalleryUrl,
            thumbnailImage:
              response.data?.furnishedImageId ||
              response.data?.thumbnailFurnishedImage ||
              response.data?.thumbnailImage ||
              response.data?.image ||
              null,
            source,
          });
          try {
            const hasToken = !!store.getState()?.roomNateSlice?.currentToken;
            if (!hasToken && newId) {
              addGuestLayout({
                uuid: newId,
                galleryUrl: normalizedGalleryUrl ?? undefined,
                source,
              });
            }
          } catch (err) {
            console.warn('[useUploadFlow] guest claim write failed (immediate):', err);
          }
          return;
        }

        throw new Error('Unexpected response format');
      } catch (error) {
        clearInterval(progressInterval);

        if (error?.name === 'CanceledError' || error?.isCancelled) {
          setState('idle');
          setSelectedFile(null);
          return;
        }

        const mappedError = mapErrorToUI(error);

        if (mappedError?.shouldDisplay) {
          setUploadError(mappedError);
          setState('error');
          trackUploadFailure(mappedError);
          eventLog(window, 'floor_plan_event', 'upload_failed', {
            errorType: mappedError.errorType,
            errorMessage: mappedError.message,
            backendError: error.response?.data?.error,
            requestId: error.response?.data?.request_id,
            fileName: file?.name,
            fileSize: file?.size != null ? formatFileSize(file.size) : undefined,
            // The file reached the server, so its artifact was captured under
            // this same session_id — stamp it so the failed row can open it.
            ...(correlationId ? { sessionId: correlationId } : {}),
          });
        } else {
          setState('idle');
        }
      }
    },
    [startPolling, source, eventLog, customerRef]
  );

  const handleFileSelect = useCallback(
    (file) => {
      setValidationError(null);
      setUploadError(null);

      const validation = validateFile(file);

      if (!validation.isValid) {
        // UX first: the uploader sees the friendly message immediately and
        // synchronously. Capture runs in the background and can never change
        // what's shown here.
        setValidationError(validation.error);
        setSelectedFile(null);
        const correlationId = captureRejectedUpload(
          file,
          validation.errorType,
          validation.error
        );
        eventLog(window, 'floor_plan_event', 'upload_failed', {
          errorType: validation.errorType,
          errorMessage: validation.error,
          fileName: file?.name,
          fileSize: file?.size != null ? formatFileSize(file.size) : undefined,
          // Present only when we actually captured the artifact, so the failed
          // row links to it; absent keeps the event byte-for-byte as before.
          ...(correlationId ? { sessionId: correlationId } : {}),
        });
        return;
      }

      setSelectedFile(file);
      setValidationError(null);

      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      setPreviewUrl(URL.createObjectURL(file));

      startUpload(file);
    },
    [startUpload, eventLog, captureRejectedUpload]
  );

  const handleCancel = useCallback(() => {
    if (uploadAbortControllerRef.current) {
      uploadAbortControllerRef.current.abort();
    }
    if (captureAbortControllerRef.current) {
      captureAbortControllerRef.current.abort();
    }
    if (pollerRef.current?.cancel) {
      pollerRef.current.cancel();
    }

    setState('idle');
    setSelectedFile(null);
    setValidationError(null);
    setUploadError(null);
    setJobId(null);
    setProcessingStatus(null);
    setElapsedTime(0);
    setUploadProgress(0);
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    setPreviewUrl(null);
  }, []);

  const handleTryAgain = useCallback(() => {
    handleCancel();
    resetAnalytics();

    setGalleryUrl(null);
    setUniqueId(null);
  }, [handleCancel]);

  useEffect(() => {
    return () => {
      if (uploadAbortControllerRef.current) {
        uploadAbortControllerRef.current.abort();
      }
      if (captureAbortControllerRef.current) {
        captureAbortControllerRef.current.abort();
      }
      if (pollerRef.current?.cancel) {
        pollerRef.current.cancel();
      }
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  return {
    state,
    selectedFile,
    previewUrl,
    validationError,
    uploadError,
    jobId,
    uniqueId,
    galleryUrl,
    processingStatus,
    elapsedTime,
    uploadProgress,
    handleFileSelect,
    handleCancel,
    handleTryAgain,
  };
};

export default useUploadFlow;
